import * as React from 'react'
import PropTypes from 'prop-types'

// --- Tiptap UI Primitive ---
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tiptap-ui/components/tiptap-ui-primitive/tooltip'

// --- Lib ---
import { cn, parseShortcutKeys } from '@tiptap-ui/lib/tiptap-utils'

import '@tiptap-ui/components/tiptap-ui-primitive/button/button-colors.scss'
import '@tiptap-ui/components/tiptap-ui-primitive/button/button-group.scss'
import '@tiptap-ui/components/tiptap-ui-primitive/button/button.scss'

export const ShortcutDisplay = ({
  shortcuts,
}) => {
  if (shortcuts.length === 0) return null

  return (
    <div>
      {shortcuts.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && <kbd>+</kbd>}
          <kbd>{key}</kbd>
        </React.Fragment>
      ))}
    </div>
  )
}

ShortcutDisplay.propTypes = {
  shortcuts: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export const Button = React.forwardRef((
  {
    className,
    children,
    tooltip,
    showTooltip = true,
    shortcutKeys,
    'aria-label': ariaLabel,
    ...props
  },
  ref
) => {
  const shortcuts = React.useMemo(() => parseShortcutKeys({ shortcutKeys }), [shortcutKeys])

  if (!tooltip || !showTooltip) {
    return (
      <button
        className={cn('tiptap-button', className)}
        ref={ref}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </button>
    )
  }

  return (
    <Tooltip delay={200}>
      <TooltipTrigger
        className={cn('tiptap-button', className)}
        ref={ref}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>
        {tooltip}
        <ShortcutDisplay shortcuts={shortcuts} />
      </TooltipContent>
    </Tooltip>
  )
})

Button.displayName = 'Button'

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  tooltip: PropTypes.node,
  showTooltip: PropTypes.bool,
  shortcutKeys: PropTypes.string,
  'aria-label': PropTypes.string,
}

export const ButtonGroup = React.forwardRef(({ className, children, orientation = 'vertical', ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('tiptap-button-group', className)}
      data-orientation={orientation}
      role='group'
      {...props}
    >
      {children}
    </div>
  )
})
ButtonGroup.displayName = 'ButtonGroup'

ButtonGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
}

export default Button
